import {
  FC,
  ChangeEvent,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Scrollbars } from 'react-custom-scrollbars'
import styles from 'styles/components/label.module.scss'
import InputText from 'components/Form/InputText'
import { RootState } from 'reducks/reducers'
import { getMaterials, MaterialType } from 'reducks/material/slice'
import {
  getMaterialCostByMaterialIds,
  MaterialCostType,
} from 'reducks/materialCost/slice'
import { getStores } from 'reducks/store/slice'
import { getPublicRecipes, RecipeType } from 'reducks/recipe/slice'
import { getDoughs, getAvailableDoughs, DoughType } from 'reducks/dough/slice'
import {
  getFillings,
  getAvailableFillings,
  FillingType,
} from 'reducks/filling/slice'
import {
  getUsedBreadIdsRecursivelyByIds,
  DataType,
  UsedData,
} from 'reducks/business/slice'
import { getWholesalers, WholesalerType } from 'reducks/wholesaler/slice'

type Props = {
  required: boolean
  type: string
  size: string
  value: string
  placeholder: string
  errorText?: string | null
  suggestType: string
  suggestList?: suggestInfo[]
  index?: number | null
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onInput?: () => void
  onSelect: (info: suggestInfo, index: number | null | undefined) => void
  readOnly?: boolean
  style?: React.CSSProperties
}

export type suggestInfo = {
  id: string
  name: string
  search_word: string // サジェスト検索する際のフィルターワード
  isUsed?: boolean // 使用中フラグ
}

const InputTextSuggestWithLabel: FC<Props> = (props) => {
  const {
    required,
    type,
    size,
    value,
    placeholder,
    errorText,
    suggestType,
    suggestList,
    index,
    onChange,
    onInput,
    onSelect,
    readOnly,
    style,
  } = props

  const dispatch = useDispatch<any>()
  const [displaySuggest, setDisplaySuggest] = useState<boolean>(false)
  const [suggestMouseEnter, setSuggestMouseEnter] = useState<boolean>(false)
  const [suggestInputTextForcus, setSuggestInputTextForcus] =
    useState<boolean>(false)
  const [suggestListInfos, setSuggestListInfos] = useState<suggestInfo[]>([])
  const [suggestListMasters, setSuggestListMasters] = useState<suggestInfo[]>(
    [],
  )
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)
  const inputContainerRef = useRef<HTMLDivElement>(null)

  const onInputTextForcus = () => {
    const newSuggestList = [...suggestListInfos]
    setSuggestListInfos(newSuggestList)
    setSuggestInputTextForcus(true)
    setDisplaySuggest(true)

    // 入力フィールドの位置を取得してドロップダウンの位置を計算
    if (inputContainerRef.current) {
      const rect = inputContainerRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }
  }

  const onInputTextBlur = () => {
    setSuggestInputTextForcus(false)
    if (!suggestMouseEnter) {
      setDisplaySuggest(false)
    }
  }

  const onSuggestMouseEnter = () => {
    setSuggestMouseEnter(true)
  }

  const onSuggestMouseLeave = () => {
    setSuggestMouseEnter(false)
  }

  const onSuggestClick = (info: suggestInfo) => {
    onSelect(info, index)
    setDisplaySuggest(false)
    onSuggestMouseLeave()
  }

  const toHiragana = (text: string) => {
    return text.replace(/[\u30A1-\u30FA]/g, (ch) =>
      String.fromCharCode(ch.charCodeAt(0) - 0x60),
    )
  }

  useEffect(() => {
    const newSuggestList = suggestListMasters.filter(
      (text) =>
        toHiragana(text.search_word)
          .toLowerCase()
          .indexOf(toHiragana(value).toLowerCase()) > -1,
    )
    setSuggestListInfos(newSuggestList)
    if (suggestInputTextForcus) {
      if (newSuggestList.length === 0) {
        setDisplaySuggest(false)
      } else {
        setDisplaySuggest(true)
      }
    }
  }, [value, suggestListMasters, suggestInputTextForcus])

  const createMaterialList = useCallback(async () => {
    // 問屋情報をサジェストする場合
    if (suggestType === 'wholesaler') {
      try {
        // 一時的にincludeInactive: trueで全ての問屋を取得
        const wholesalersPayload = await dispatch(
          getWholesalers({ includeInactive: true }),
        )
        const wholesalers: WholesalerType[] = wholesalersPayload.payload || []
        const suggestList: suggestInfo[] = []
        wholesalers.forEach((wholesaler) => {
          suggestList.push({
            id: wholesaler.id,
            name: wholesaler.name,
            search_word: `${wholesaler.id},${wholesaler.name}`,
          })
        })
        console.log('[createMaterialList] サジェストリスト:', suggestList)
        setSuggestListMasters(suggestList)
        setSuggestListInfos(suggestList)
      } catch (error) {
        console.error('問屋情報の取得に失敗しました:', error)
        setSuggestListMasters([])
        setSuggestListInfos([])
      }
      return
    }
    // 商品・生地・フィリング情報をサジェストする場合
    if (suggestType === 'public_recipes') {
      const recipesPayload = await dispatch(getPublicRecipes())
      const recipes: RecipeType[] = recipesPayload.payload
      const suggestList: suggestInfo[] = []
      recipes.forEach((recipe) => {
        suggestList.push({
          id: recipe.id,
          name: recipe.recipe_name,
          search_word: recipe.recipe_name,
        })
      })
      setSuggestListMasters(suggestList)
      setSuggestListInfos(suggestList)
      return
    } else if (suggestType === 'available_doughs') {
      const doughsPayload = await dispatch(getAvailableDoughs())
      const doughs: DoughType[] = doughsPayload.payload
      const suggestList: suggestInfo[] = []
      doughs.forEach((dough) => {
        suggestList.push({
          id: dough.id,
          name: dough.pettern_name,
          search_word: dough.pettern_name,
        })
      })
      setSuggestListMasters(suggestList)
      setSuggestListInfos(suggestList)
      return
    } else if (suggestType === 'available_fillings') {
      const fillingsPayload = await dispatch(getAvailableFillings())
      const fillings: FillingType[] = fillingsPayload.payload
      const suggestList: suggestInfo[] = []
      fillings.forEach((filling) => {
        suggestList.push({
          id: filling.id,
          name: filling.pettern_name,
          search_word: filling.pettern_name,
        })
      })
      setSuggestListMasters(suggestList)
      setSuggestListInfos(suggestList)
      return
    } else if (suggestType === 'supplier_company_name') {
      const materialCostsPayload = await dispatch(
        getMaterialCostByMaterialIds({ ids: null }),
      )
      const materialCosts: MaterialCostType[] = materialCostsPayload.payload
      const suggestList: suggestInfo[] = []
      const supplierCompanyNameCheckList: string[] = []
      materialCosts.forEach((materialCost) => {
        materialCost.suppliers.forEach((supplier, i) => {
          if (
            !supplierCompanyNameCheckList.includes(
              supplier.supplier_company_name,
            )
          ) {
            suggestList.push({
              id: materialCost.id + i,
              name: supplier.supplier_company_name,
              search_word: supplier.supplier_company_name,
            })
            supplierCompanyNameCheckList.push(supplier.supplier_company_name)
          }
        })
      })
      setSuggestListMasters(suggestList)
      setSuggestListInfos(suggestList)
    }
    // 以下原材料情報をサジェストする場合
    const materialPayload = await dispatch(getMaterials())
    const materials: MaterialType[] = materialPayload.payload
    // 使用中か否か情報を取得する
    const materialIds: string[] = materials.map((material) => {
      return material.id
    })
    const materialUsedInfosPayload = await dispatch(
      getUsedBreadIdsRecursivelyByIds({
        ids: materialIds,
        dataType: DataType.MATERIAL,
      }),
    )
    const materialUsedInfos: UsedData[] = materialUsedInfosPayload.payload
    const usedMaterialIds: string[] =
      materialUsedInfos?.map((materialUsedInfo) => {
        return materialUsedInfo.breadIdList.length > 0
          ? materialUsedInfo.id
          : ''
      }) || []
    // const categoriesPayload: CategoryType[] = categories.payload
    const materialManufacturerSuggestList: suggestInfo[] = []
    const materialManufacturerCheckList: string[] = []
    const productNameUsedSuggestList: suggestInfo[] = []
    const productNameNotUsedSuggestList: suggestInfo[] = []
    const productNameCheckList: string[] = []
    materials.forEach((material) => {
      if (!materialManufacturerCheckList.includes(material.manufacturer)) {
        materialManufacturerSuggestList.push({
          id: material.id,
          name: material.manufacturer,
          search_word: material.manufacturer,
        })
        materialManufacturerCheckList.push(material.manufacturer)
      }
      if (!productNameCheckList.includes(material.product_name)) {
        if (usedMaterialIds.includes(material.id)) {
          productNameUsedSuggestList.push({
            id: material.id,
            name: material.product_name + ' ' + material.manufacturer,
            search_word:
              material.product_name +
              ',' +
              material.manufacturer +
              ',' +
              material.product_name_kana +
              ',' +
              material.notation_fluctuation.join(','),
            isUsed: true,
          })
        } else {
          productNameNotUsedSuggestList.push({
            id: material.id,
            name: material.product_name + ' ' + material.manufacturer,
            search_word:
              material.product_name +
              ',' +
              material.manufacturer +
              ',' +
              material.product_name_kana +
              ',' +
              material.notation_fluctuation.join(','),
            isUsed: false,
          })
        }
        productNameCheckList.push(
          material.product_name + ' ' + material.manufacturer,
        )
      }
    })
    const productNameSuggestList: suggestInfo[] = [
      ...productNameUsedSuggestList,
      ...productNameNotUsedSuggestList,
    ]
    if (suggestType === 'material_manufacturer') {
      setSuggestListMasters(materialManufacturerSuggestList)
      setSuggestListInfos(materialManufacturerSuggestList)
    } else if (suggestType === 'material_product_name') {
      setSuggestListMasters(productNameSuggestList)
      setSuggestListInfos(productNameSuggestList)
    } else if (suggestType === 'material_used_product_name') {
      setSuggestListMasters(productNameUsedSuggestList)
      setSuggestListInfos(productNameUsedSuggestList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, suggestType])

  const { stores } = useSelector((state: RootState) => state.storeSlice)
  const createStoreList = useCallback(async () => {
    await dispatch(getStores())
    const storeNameSuggestList: suggestInfo[] = []
    const storeNameCheckList: string[] = []
    stores.forEach((store) => {
      if (!storeNameCheckList.includes(store.name)) {
        storeNameSuggestList.push({
          id: store.store_id,
          name: store.name,
          search_word: store.name,
        })
        storeNameCheckList.push(store.name)
      }
    })
    if (suggestType === 'store_name') {
      setSuggestListMasters(storeNameSuggestList)
      setSuggestListInfos(storeNameSuggestList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, suggestType])

  const createRecipeList = useCallback(async () => {
    const recipePayload = await dispatch(getPublicRecipes())
    const recipes = recipePayload.payload
    const recipeNameSuggestList: suggestInfo[] = []
    const recipeNameCheckList: string[] = []
    recipes.forEach((recipe: RecipeType) => {
      if (!recipeNameCheckList.includes(recipe.recipe_name)) {
        recipeNameSuggestList.push({
          id: recipe.id,
          name: recipe.recipe_name,
          search_word: recipe.recipe_name,
        })
        recipeNameCheckList.push(recipe.recipe_name)
      }
    })
    setSuggestListMasters(recipeNameSuggestList)
    setSuggestListInfos(recipeNameSuggestList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, suggestType])

  const { doughs } = useSelector((state: RootState) => state.doughSlice)
  const createDoughList = useCallback(async () => {
    await dispatch(getDoughs())
    const doughNameSuggestList: suggestInfo[] = []
    const doughNameCheckList: string[] = []
    doughs.forEach((dough) => {
      if (!doughNameCheckList.includes(dough.pettern_name)) {
        doughNameSuggestList.push({
          id: dough.id,
          name: dough.pettern_name,
          search_word: dough.pettern_name,
        })
        doughNameCheckList.push(dough.pettern_name)
      }
    })
    setSuggestListMasters(doughNameSuggestList)
    setSuggestListInfos(doughNameSuggestList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, suggestType])

  const { fillings } = useSelector((state: RootState) => state.fillingSlice)
  const createFillingList = useCallback(async () => {
    await dispatch(getFillings())
    const fillingNameSuggestList: suggestInfo[] = []
    const fillingNameCheckList: string[] = []
    fillings.forEach((filling) => {
      if (!fillingNameCheckList.includes(filling.pettern_name)) {
        fillingNameSuggestList.push({
          id: filling.id,
          name: filling.pettern_name,
          search_word: filling.pettern_name,
        })
        fillingNameCheckList.push(filling.pettern_name)
      }
    })
    setSuggestListMasters(fillingNameSuggestList)
    setSuggestListInfos(fillingNameSuggestList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, suggestType])

  useLayoutEffect(() => {
    createMaterialList()
    if (suggestType === 'store_name') {
      createStoreList()
    }
    if (suggestType === 'recipe_name') {
      createRecipeList()
    }
    if (suggestType === 'dough_name') {
      createDoughList()
    }
    if (suggestType === 'filling_name') {
      createFillingList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestType])

  useEffect(() => {
    if (suggestList) {
      setSuggestListMasters(suggestList)
      setSuggestListInfos(suggestList)
    }
  }, [suggestList])

  // ドロップダウンをPortalとして描画
  const renderDropdown = () => {
    if (!displaySuggest || readOnly || !dropdownPosition) return null

    return createPortal(
      <div
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: '430px',
          maxWidth: '670px',
          maxHeight: '290px',
          borderRadius: '4px',
          border: '2px solid #ddd',
          background: '#fff',
          zIndex: 10000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
        onMouseEnter={onSuggestMouseEnter}
        onMouseLeave={onSuggestMouseLeave}
      >
        <Scrollbars style={{ width: 430, height: 150 }}>
          {suggestListInfos.map((suggestText) => (
            <div
              key={suggestText.name}
              className={styles.inputtext_suggest_text}
              onClick={() => onSuggestClick(suggestText)}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                {suggestText.isUsed && (
                  <span className={styles.inputtext_suggest_used_label}>
                    使用中
                  </span>
                )}
                <span>{suggestText.name}</span>
              </div>
            </div>
          ))}
        </Scrollbars>
      </div>,
      document.body,
    )
  }

  return (
    <div ref={inputContainerRef}>
      <InputText
        required={required}
        type={type}
        size={size}
        value={value}
        placeholder={placeholder}
        rightAligned={false}
        errorText={errorText}
        onChange={(e) => (onChange ? onChange(e) : null)}
        onInput={onInput}
        onFocus={onInputTextForcus}
        onBlur={onInputTextBlur}
        readOnly={readOnly}
        style={style}
      />
      {renderDropdown()}
    </div>
  )
}

export default InputTextSuggestWithLabel
